import { ColumnData, SpreadsheetNoteGet } from 'api';
import { PageType, PageTypeKey } from 'components/Notes/types';
import {
  ConfigurationCell,
  ConfigurationCellMap,
  EvaluatedCellSheetMap,
  ScalarSpreadsheetConfigDataInterface
} from 'models/interfaces/spreadsheet-types/core-types';
import { IWorkbook, OnChangeFn } from 'models/interfaces/spreadsheet-types/workbook-types';
import { AttributeReferenceConfig } from 'pages/Financials/financials-widget-drawer/types';

export interface SpreadsheetNotePersistedCell {
  value: string;
  expr: string;
  key: string;
}
export type SpreadsheetNotePersistedCellMap = Record<string, SpreadsheetNotePersistedCell>;

export type SpreadsheetNoteData = Omit<SpreadsheetNoteGet, 'author' | 'updated_by'> & {
  temp_id?: string;
  isNew?: boolean
  xlsxPresentationImages?: SpreadsheetNoteXlsxImage[];
}

export type SpreadsheetNoteXlsxImageAnchor = {
  column: number;
  columnOffsetPx: number;
  row: number;
  rowOffsetPx: number;
};

export type SpreadsheetNoteXlsxImage = {
  alt: string;
  contentType: string;
  from: SpreadsheetNoteXlsxImageAnchor;
  id: string;
  src: string;
  size?: {
    heightPx: number;
    widthPx: number;
  };
  to?: SpreadsheetNoteXlsxImageAnchor;
};

export type SpreadsheetNoteTableData = {
  attributeReferenceConfig?: AttributeReferenceConfig;
  isAttributeMapperEnabled: boolean
  sheetName?: string;
  documentId?: number;
}

export type SpreadsheetNotesProps = {
  pageTypeKey: PageTypeKey;
  pageType: PageType;
  pageTypeId?: number;
  notes: SpreadsheetNoteData[] | null;
  isApproach?: boolean;
  isDisabled?: boolean;
  setNotes: React.Dispatch<React.SetStateAction<SpreadsheetNoteData[]>>;
  saveNotes?: () => void;
  handleOnDeleteNote(note: SpreadsheetNoteData): void;
};

export type SpreadsheetNoteListProps = {
  pageTypeKey: PageTypeKey;
  pageType: PageType;
  pageTypeId: number;
  notes: SpreadsheetNoteData[] | null;
  setNotes: React.Dispatch<React.SetStateAction<SpreadsheetNoteData[]>>
  isDisabled?: boolean;
  handleOnDeleteNote: (note: SpreadsheetNoteData) => void;
};

export type SpreadsheetNoteItemProps = {
  note: ScalarSpreadsheetConfigDataInterface<
    SpreadsheetNoteData,
    ColumnData,
    SpreadsheetNoteTableData,
    SpreadsheetNoteCellData
  >;
  isDisabled?: boolean;
  workbook?: IWorkbook;
  onChange: OnChangeFn;
  isAttributeMapperEnabled: boolean;
  cells?: EvaluatedCellSheetMap<SpreadsheetNoteCellData, SpreadsheetNoteData, ColumnData, SpreadsheetNoteTableData>;
  parentContainerHeight: number;
  isXlsxPreview?: boolean;
  xlsxPreviewOptions?: SpreadsheetNoteDisplayOptions;
};

export type SpreadsheetNoteDisplayOptions = {
  showGridLegends?: boolean;
  allowColumnResize?: boolean;
  allowRowResize?: boolean;
  allowMergedSelectionIndicatorSpan?: boolean;
  disableLegendSelection?: boolean;
  enableCopyFeedback?: boolean;
  reserveColumnIndicatorSpace?: boolean;
  resizeHandlesInColumnLegend?: boolean;
};

export const referenceColumnLegend = 'α'

export const RowReferenceType = 'row';
export const ColumnReferenceType = 'column';
export const AlphaReferenceType = 'alpha';

export const ORDER_OF_MAGNITUDES: { label: string, value: number }[] = [{
  label: '1',
  value: 1,
},
{
  label: '1,000',
  value: 1_000,
},
{
  label: '1,000,000',
  value: 1_000_000,
},
]

export interface SpreadsheetNoteCellData {
  referenceType?: 'row' | 'column' | 'alpha';
  sheetName?: string;
}

export interface SpreadsheetNoteConfigCell extends ConfigurationCell<SpreadsheetNoteCellData, SpreadsheetNoteData, ColumnData, SpreadsheetNoteTableData> { }

export interface SpreadsheetNoteConfigCellMap extends ConfigurationCellMap<SpreadsheetNoteCellData, SpreadsheetNoteData, ColumnData, SpreadsheetNoteTableData> { }
